/**
 * Admin API for user management
 */
import { auth } from '@/lib/picasso-firebase';

const API_BASE_URL = import.meta.env.VITE_BASE_URL;

/**
 * Get authorization header with Firebase ID token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

/**
 * Fetch all pending users
 */
export async function fetchPendingUsers(): Promise<any[]> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE_URL}/picasso/admin/users/pending`, {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch pending users' }));
    throw new Error(error.detail || 'Failed to fetch pending users');
  }
  
  return response.json();
}

/**
 * Fetch all approved users
 */
export async function fetchApprovedUsers(): Promise<any[]> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE_URL}/picasso/admin/users/approved`, {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch approved users' }));
    throw new Error(error.detail || 'Failed to fetch approved users');
  }
  
  return response.json();
}

/**
 * Approve a pending user
 */
export async function approveUser(user_id: string, adminEmail: string): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/picasso/admin/users/approve`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      user_id,
      admin_email: adminEmail,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to approve user' }));
    throw new Error(error.detail || 'Failed to approve user');
  }
  
  return response.json();
}

/**
 * Reject a pending user
 */
export async function rejectUser(user_id: string): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/picasso/admin/users/reject`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      user_id,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to reject user' }));
    throw new Error(error.detail || 'Failed to reject user');
  }
  
  return response.json();
}

/**
 * Delete an approved user
 */
export async function deleteUser(user_id: string): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/picasso/admin/users/${user_id}`, {
    method: 'DELETE',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete user' }));
    throw new Error(error.detail || 'Failed to delete user');
  }
  
  return response.json();
}
